import { isFunction } from "lodash"
import { FactorRiskDriftWithTooltip, FactorPerformanceDriftWithTooltip, FactorBetaDriftWithTooltip } from "components/drift/driftWithTooltip"
import { VALUE_FORMATS, METRIC_TYPES } from "./constants"

const FACTOR_NORMALIZED_RETURN_DATA_PATH = "performance.normalizedReturn"

function getFactorRiskMetrics({ isEndVsStartDateComparison = false }) {
  return [
    FACTOR_METRICS_BY_ID.factorRiskDecomposition,
    FACTOR_METRICS_BY_ID.factorValueAtRisk,
    FACTOR_METRICS_BY_ID.factorExposure,
    FACTOR_METRICS_BY_ID.factorNetExposure,
    FACTOR_METRICS_BY_ID.factorPredictedRisk(isEndVsStartDateComparison),
    FACTOR_METRICS_BY_ID.historicalFactorSensitivity,
    FACTOR_METRICS_BY_ID.predictedFactorSensitivity,
  ]
}

function getFactorContentSetMetrics({ contentSetId, isSensitivity }) {
  return [
    FACTOR_METRICS_BY_ID.factorContentSetNetContribution(contentSetId),
    FACTOR_METRICS_BY_ID.factorContentSetCurrencyExposure({ contentSetId, isSensitivity }),
  ]
}

function getFactorBetaMetrics({ isActive }) {
  return [
    FACTOR_METRICS_BY_ID.beta({ isActive }),
  ]
}

function getFactorPerformanceMetrics({ selectedMetricDisplay, filterInstrumentColumns, selectedPeriodPerformance }) {
  const format = selectedMetricDisplay === METRIC_TYPES.CUMULATIVE_PERCENT ? VALUE_FORMATS.PERCENT : VALUE_FORMATS.CURRENCY
  const filterPerformanceChangeColumns = (isOneDayPerformanceChange) => (instrumentColumns) => {
    const isOneDayPerformanceSelected = selectedPeriodPerformance === "1D"
    if (isOneDayPerformanceChange && !isOneDayPerformanceSelected) {
      return []
    } else if (!isOneDayPerformanceChange && isOneDayPerformanceSelected) {
      return []
    }
    return filterInstrumentColumns(instrumentColumns)
  }

  return [
    FACTOR_METRICS_BY_ID.factorCumulativeReturn(format, selectedMetricDisplay, filterInstrumentColumns),
    FACTOR_METRICS_BY_ID.factorAnnualizedReturns(filterInstrumentColumns),
    FACTOR_METRICS_BY_ID.factorAnnualizedVolatility(filterInstrumentColumns),
    FACTOR_METRICS_BY_ID.factorInformationRatio(filterInstrumentColumns),
    FACTOR_METRICS_BY_ID.factorPerformanceNetExposure(filterInstrumentColumns),
    FACTOR_METRICS_BY_ID.factorPercentPriceChange1Day(filterPerformanceChangeColumns(true)),
    FACTOR_METRICS_BY_ID.factorPercentPriceChangeCumulative(filterPerformanceChangeColumns(false)),
    FACTOR_METRICS_BY_ID.factorNormalizedReturn(filterInstrumentColumns),
  ]
}

export const FACTOR_METRIC_CATEGORIES = {
  factorRisk: getFactorRiskMetrics,
  factorPerformance: getFactorPerformanceMetrics,
  factorBeta: getFactorBetaMetrics,
  factorContentSet: getFactorContentSetMetrics,
}

export const FACTOR_METRICS_BY_ID = {
  factorLabel: {
    id: "label",
    label: "Factor",
    downloadOnly: true,
    onlyShowOnceInDownload: true,
    dataGetter: (_, { label }) => label,
  },
  factorRiskDecomposition: {
    id: "riskDecomposition",
    label: "% Risk decomp",
    format: VALUE_FORMATS.PERCENT,
    dataPath: "risk.riskDecomposition",
    enhanceValue: (item, value, metric) => <FactorRiskDriftWithTooltip item={item} value={value} metric={metric} label="% Risk decomp" />,
  },
  factorValueAtRisk: {
    id: "valueAtRisk",
    label: "Uncorrelated VaR ($)",
    format: VALUE_FORMATS.CURRENCY,
    dataPath: "risk.valueAtRisk",
  },
  factorExposure: {
    id: "exposure",
    label: "USD exposure",
    format: VALUE_FORMATS.CURRENCY,
    dataPath: "risk.exposure",
    enhanceValue: (item, value, metric) => <FactorRiskDriftWithTooltip item={item} value={value} metric={metric} label="USD exposure" />,
  },
  factorNetExposure: {
    id: "netExposure",
    label: "Net Exposure",
    format: VALUE_FORMATS.NUMBER,
    dataPath: "risk.netExposure",
    enhanceValue: (item, value, metric) => <FactorRiskDriftWithTooltip item={item} value={value} metric={metric} label="Net exposure" />,
  },
  factorPredictedRisk: (isEndVsStartDateComparison = false) => ({
    id: "predictedRisk",
    label: "Factor Predicted Risk %",
    format: VALUE_FORMATS.PERCENT_100,
    dataPath: "risk.predictedRisk",
    tooltipInstrumentOverride: "Standalone",
    hideInstrumentName: !isEndVsStartDateComparison,
    filterInstrumentColumns: (instrumentColumns) => isEndVsStartDateComparison ? instrumentColumns : instrumentColumns.filter(column => column === "original"),
    enhanceValue: (item, value, metric) => <FactorRiskDriftWithTooltip item={item} value={value} metric={metric} label="Predicted risk" />,
  }),
  factorCumulativeReturn: (format, selectedMetricDisplay, filterInstrumentColumns = false) => ({
    id: "cumulativeReturn",
    label: selectedMetricDisplay === METRIC_TYPES.CUMULATIVE_PERCENT ? "Return %" : "Return $",
    format,
    dataPath: "performance.cumulativeReturn",
    filterInstrumentColumns,
  }),
  factorAnnualizedReturns: (filterInstrumentColumns = false) => ({
    id: "annualizedReturns",
    label: "Ann Return %",
    format: VALUE_FORMATS.PERCENT_100,
    dataPath: "performance.annualizedReturns",
    filterInstrumentColumns,
  }),
  factorAnnualizedVolatility: (filterInstrumentColumns = false) => ({
    id: "annualizedVolatility",
    label: "Ann Vol %",
    format: VALUE_FORMATS.PERCENT_100,
    dataPath: "performance.annualizedVolatility",
    filterInstrumentColumns,
  }),
  factorInformationRatio: (filterInstrumentColumns = false) => ({
    id: "informationRatio",
    label: "Risk-Adjusted Performance",
    format: VALUE_FORMATS.NUMBER,
    dataPath: "performance.informationRatio",
    filterInstrumentColumns,
  }),
  factorPercentPriceChange1Day: (filterInstrumentColumns = false) => ({
    id: "percentPriceChange1Day",
    label: "Factor Return %",
    format: VALUE_FORMATS.PERCENT,
    dataPath: "performance.percentPriceChange1Day",
    hideInstrumentName: true,
    tooltipInstrumentOverride: "Standalone",
    hideIfEmpty: true,
    filterInstrumentColumns: filterFactorReturnColumns(filterInstrumentColumns),
    enhanceValue: (item, value) => <FactorPerformanceDriftWithTooltip item={item} value={value} dataPath={FACTOR_NORMALIZED_RETURN_DATA_PATH} />,
  }),
  factorPercentPriceChangeCumulative: (filterInstrumentColumns = false) => ({
    id: "percentPriceChangeCumulative",
    label: "Factor Return %",
    format: VALUE_FORMATS.PERCENT,
    dataPath: "performance.percentPriceChangeCumulative",
    hideInstrumentName: true,
    tooltipInstrumentOverride: "Standalone",
    hideIfEmpty: true,
    filterInstrumentColumns: filterFactorReturnColumns(filterInstrumentColumns),
    enhanceValue: (item, value) => <FactorPerformanceDriftWithTooltip item={item} value={value} dataPath={FACTOR_NORMALIZED_RETURN_DATA_PATH} />,
  }),
  factorNormalizedReturn: (filterInstrumentColumns = false) => ({
    id: "normalizedReturn",
    label: "Normalized Return",
    format: VALUE_FORMATS.NUMBER,
    dataPath: FACTOR_NORMALIZED_RETURN_DATA_PATH,
    filterInstrumentColumns,
    downloadOnly: true,
  }),
  factorPerformanceNetExposure: (filterInstrumentColumns = false) => ({
    id: "netExposure",
    label: "Net Exposure",
    format: VALUE_FORMATS.NUMBER,
    dataPath: "performance.netExposure",
    filterInstrumentColumns,
    enhanceValue: (item, value, metric) => <FactorRiskDriftWithTooltip item={item} value={value} metric={metric} label="Net exposure" />,
  }),
  factorContentSetNetContribution: (contentSetId) => ({
    id: "contentSetNetContribution",
    label: "Net Contribution",
    format: VALUE_FORMATS.NUMBER,
    dataPath: `${contentSetId}.netContribution`,
  }),
  factorContentSetCurrencyExposure: ({ contentSetId, isSensitivity = false }) => ({
    id: "contentSetCurrencyExposure",
    label: "Currency Exposure",
    format: VALUE_FORMATS.NUMBER,
    shouldHide: !isSensitivity,
    dataPath: `${contentSetId}.currencyExposure`,
  }),
  beta: ({ isActive }) => ({
    id: "beta",
    label: `Beta (${isActive ? "Active" : "Absolute"})`,
    format: VALUE_FORMATS.NUMBER,
    enhanceValue: (item, value, metric) => <FactorBetaDriftWithTooltip item={item} value={value} metric={metric} />,
    dataPath: "beta.value",
  }),
  historicalFactorSensitivity: {
    id: "historicalFactorSensitivity",
    label: "Historical Factor Sensitivity",
    format: VALUE_FORMATS.PERCENT_100,
    dataPath: "sensitivity.historical",
  },
  predictedFactorSensitivity: {
    id: "predictedFactorSensitivity",
    label: "Predicted Factor Sensitivity",
    format: VALUE_FORMATS.PERCENT_100,
    dataPath: "sensitivity.predicted",
  },
}

function filterFactorReturnColumns(filterInstrumentColumns) {
  return (instrumentColumns) => {
    if (!isFunction(filterInstrumentColumns)) {
      return instrumentColumns
    }
    const filteredColumns = filterInstrumentColumns(instrumentColumns)
    return filteredColumns?.[0] ? [filteredColumns[0]] : []
  }
}
